import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Container from "../../../componentLibrary/Container";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import ExternalTextEditor from "../../../componentLibrary/ExternalTextEditor";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import ConfirmationBox from "../../../componentLibrary/ConfirmationBox";
import { useNotification } from "../../../hooks/useNotification";
import {
  useUpdateAnnouncement,
  useDeleteAnnouncement,
} from "../../../api/mutations";
import {
  AnnouncementI,
  AnnouncementTypeEnum,
  AnnouncementPriorityEnum,
} from "../../../types";
import { UpdateAnnouncementRequest } from "../../../api/types";
import AnnouncementModal from "../../../components/AnnouncementModal";
import { isQuillContentEmpty } from "../../../utils/quillUtils";

interface UpdatedAnnouncementState {
  _id: string;
  title: string;
  message: string;
  type: AnnouncementTypeEnum;
  priority: AnnouncementPriorityEnum;
  actions: Array<{
    label: string;
    url?: string;
    variant?: "primary" | "secondary";
  }>;
  expiresAt: string;
  startsAt: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

const blankAnnouncement: UpdatedAnnouncementState = {
  _id: "",
  title: "",
  message: "",
  type: AnnouncementTypeEnum.INFO,
  priority: AnnouncementPriorityEnum.MEDIUM,
  actions: [],
  expiresAt: "",
  startsAt: moment().startOf("hour").format("YYYY-MM-DDTHH:mm"),
  createdBy: "",
  createdAt: new Date(),
  updatedAt: new Date(),
};

const EditAnnouncementComponent = ({
  originalAnnouncementData,
  isEdit,
  setIsEdit,
}: {
  originalAnnouncementData: AnnouncementI;
  isEdit: boolean;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
}) => {
  const [updatedAnnouncement, setUpdatedAnnouncement] =
    useState<UpdatedAnnouncementState>(blankAnnouncement);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const queryClient = useQueryClient();
  const { notify } = useNotification();

  useEffect(() => {
    setUpdatedAnnouncement({
      _id: originalAnnouncementData._id || "",
      title: originalAnnouncementData.title || "",
      message: originalAnnouncementData.message || "",
      type: originalAnnouncementData.type || AnnouncementTypeEnum.INFO,
      priority:
        originalAnnouncementData.priority || AnnouncementPriorityEnum.MEDIUM,
      actions: originalAnnouncementData.actions || [],
      startsAt: moment(originalAnnouncementData.startsAt).format(
        "YYYY-MM-DDTHH:mm",
      ),
      expiresAt: originalAnnouncementData.expiresAt
        ? moment(originalAnnouncementData.expiresAt).format("YYYY-MM-DDTHH:mm")
        : "",
      createdBy: originalAnnouncementData.createdBy || "",
      createdAt: originalAnnouncementData.createdAt || new Date(),
      updatedAt: originalAnnouncementData.updatedAt || new Date(),
    });
  }, [originalAnnouncementData]);

  const updateMutation = useUpdateAnnouncement({
    onSuccess: async () => {
      notify("success", "Announcement updated successfully");
      setIsEdit(false);
      await queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
    onError: () => {
      notify("error", "Failed to update announcement");
    },
  });

  const deleteMutation = useDeleteAnnouncement({
    onSuccess: async () => {
      notify("success", "Announcement deleted successfully");
      await queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
    onError: () => {
      notify("error", "Failed to delete announcement");
    },
  });

  const handleUpdate = () => {
    if (!updatedAnnouncement.title.trim()) {
      notify("error", "Title is required");
      return;
    }
    if (isQuillContentEmpty(updatedAnnouncement.message)) {
      notify("error", "Message is required");
      return;
    }

    // ✅ UPDATE: Use proper type and ISO strings
    const payload: UpdateAnnouncementRequest = {
      title: updatedAnnouncement.title,
      message: updatedAnnouncement.message,
      type: updatedAnnouncement.type,
      priority: updatedAnnouncement.priority,
      actions: updatedAnnouncement.actions.filter((action) =>
        action.label.trim(),
      ),
      expiresAt: updatedAnnouncement.expiresAt
        ? new Date(updatedAnnouncement.expiresAt).toISOString()
        : null,
      startsAt: new Date(updatedAnnouncement.startsAt).toISOString(),
    };

    updateMutation.mutate({
      id: originalAnnouncementData._id,
      body: payload,
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(originalAnnouncementData._id);
    setShowDeleteDialog(false);
  };

  const handleCancelEdit = () => {
    setUpdatedAnnouncement({
      _id: originalAnnouncementData._id || "",
      title: originalAnnouncementData.title || "",
      message: originalAnnouncementData.message || "",
      type: originalAnnouncementData.type || AnnouncementTypeEnum.INFO,
      priority:
        originalAnnouncementData.priority || AnnouncementPriorityEnum.MEDIUM,
      actions: originalAnnouncementData.actions || [],
      startsAt: moment(originalAnnouncementData.startsAt).format(
        "YYYY-MM-DDTHH:mm",
      ),
      expiresAt: originalAnnouncementData.expiresAt
        ? moment(originalAnnouncementData.expiresAt).format("YYYY-MM-DDTHH:mm")
        : "",
      createdBy: originalAnnouncementData.createdBy || "",
      createdAt: originalAnnouncementData.createdAt || new Date(),
      updatedAt: originalAnnouncementData.updatedAt || new Date(),
    });
    setIsEdit(false);
    setShowCancelDialog(false);
  };

  const handleAddAction = () => {
    setUpdatedAnnouncement((prev) => ({
      ...prev,
      actions: [...prev.actions, { label: "", url: "", variant: "primary" }],
    }));
  };

  const handleRemoveAction = (index: number) => {
    setUpdatedAnnouncement((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  const handleActionChange = (
    index: number,
    field: "label" | "url" | "variant",
    value: string,
  ) => {
    setUpdatedAnnouncement((prev) => {
      const newActions = [...prev.actions];
      newActions[index] = { ...newActions[index], [field]: value };
      return { ...prev, actions: newActions };
    });
  };

  const getStatusBadge = () => {
    const now = moment();
    const startsAtMoment = moment(originalAnnouncementData.startsAt);
    const expiresAtMoment = originalAnnouncementData.expiresAt
      ? moment(originalAnnouncementData.expiresAt)
      : null;

    if (now.isBefore(startsAtMoment)) {
      return "bg-blue-100 text-blue-800";
    }
    if (expiresAtMoment && now.isAfter(expiresAtMoment)) {
      return "bg-gray-100 text-gray-800";
    }
    return "bg-green-100 text-green-800";
  };

  const getStatusText = () => {
    const now = moment();
    const startsAtMoment = moment(originalAnnouncementData.startsAt);
    const expiresAtMoment = originalAnnouncementData.expiresAt
      ? moment(originalAnnouncementData.expiresAt)
      : null;

    if (now.isBefore(startsAtMoment)) {
      return "Upcoming";
    }
    if (expiresAtMoment && now.isAfter(expiresAtMoment)) {
      return "Expired";
    }
    return "Active";
  };

  return (
    <>
      <Container className="flex flex-col h-[83vh] relative overflow-hidden">
        {/* Sticky Header with Edit/Save buttons - always visible at top */}
        <header
          className="sticky top-0 z-10 bg-white flex flex-row justify-between items-center pb-3 border-b border-gray-200"
          aria-label="Announcement editor toolbar"
        >
          <h3 className="mb-0">Announcement</h3>
          <div className="flex flex-row space-x-2 items-center">
            {isEdit ? (
              <>
                <Button
                  variant="alternative"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <span className="text-red-400">Cancel</span>
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={updateMutation.isPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button variant="alternative" onClick={() => setIsEdit(true)}>
                  <PencilSquareIcon className="w-6 h-6" />
                </Button>
                <Button
                  variant="cautionary"
                  onClick={() => setShowDeleteDialog(true)}
                  className="flex items-center gap-2"
                >
                  <TrashIcon className="h-5 w-5" />
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              onClick={() => setShowPreviewModal(true)}
            >
              Preview
            </Button>
          </div>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Metadata */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-semibold">Created:</span>{" "}
                {moment(originalAnnouncementData.createdAt).format(
                  "MMM D, YYYY h:mm A",
                )}
              </div>
              <div>
                <span className="font-semibold">Updated:</span>{" "}
                {moment(originalAnnouncementData.updatedAt).format(
                  "MMM D, YYYY h:mm A",
                )}
              </div>
              <div>
                <span className="font-semibold">Created By:</span>{" "}
                {originalAnnouncementData.createdBy}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">Status:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge()}`}
                >
                  {getStatusText()}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <Input
                value={updatedAnnouncement.title}
                onChange={(e) =>
                  setUpdatedAnnouncement((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                disabled={!isEdit}
                placeholder="Enter announcement title"
              />
            </div>

            <div>
              <ExternalTextEditor
                value={updatedAnnouncement.message}
                handleChange={(value) =>
                  setUpdatedAnnouncement((prev) => ({
                    ...prev,
                    message: value,
                  }))
                }
                disabled={!isEdit}
                placeholder="Enter announcement message"
                label="Message"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <Combobox
                  options={[
                    { value: AnnouncementTypeEnum.INFO, label: "Info" },
                    { value: AnnouncementTypeEnum.UPDATE, label: "Update" },
                    { value: AnnouncementTypeEnum.FEATURE, label: "Feature" },
                  ]}
                  value={{
                    value: updatedAnnouncement.type,
                    label:
                      updatedAnnouncement.type.charAt(0).toUpperCase() +
                      updatedAnnouncement.type.slice(1),
                  }}
                  onChange={(option: ComboboxOptionI) =>
                    setUpdatedAnnouncement((prev) => ({
                      ...prev,
                      type: option.value as AnnouncementTypeEnum,
                    }))
                  }
                  disabled={!isEdit}
                  placeholder="Select type..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <Combobox
                  options={[
                    { value: AnnouncementPriorityEnum.LOW, label: "Low" },
                    { value: AnnouncementPriorityEnum.MEDIUM, label: "Medium" },
                    { value: AnnouncementPriorityEnum.HIGH, label: "High" },
                    {
                      value: AnnouncementPriorityEnum.CRITICAL,
                      label: "Critical",
                    },
                  ]}
                  value={{
                    value: updatedAnnouncement.priority,
                    label:
                      updatedAnnouncement.priority.charAt(0).toUpperCase() +
                      updatedAnnouncement.priority.slice(1),
                  }}
                  onChange={(option: ComboboxOptionI) =>
                    setUpdatedAnnouncement((prev) => ({
                      ...prev,
                      priority: option.value as AnnouncementPriorityEnum,
                    }))
                  }
                  disabled={!isEdit}
                  placeholder="Select priority..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Starts At
                </label>
                <Input
                  type="datetime-local"
                  value={updatedAnnouncement.startsAt}
                  onChange={(e) =>
                    setUpdatedAnnouncement((prev) => ({
                      ...prev,
                      startsAt: e.target.value,
                    }))
                  }
                  disabled={!isEdit}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expires At (Optional)
                </label>
                <Input
                  type="datetime-local"
                  value={updatedAnnouncement.expiresAt}
                  onChange={(e) =>
                    setUpdatedAnnouncement((prev) => ({
                      ...prev,
                      expiresAt: e.target.value,
                    }))
                  }
                  disabled={!isEdit}
                />
              </div>
            </div>

            <div className="flex flex-col bg-[#F5F5F5] rounded-lg p-4 space-y-2 shadow-md border border-[#E0E0E0]">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-semibold">Action Buttons</h4>
                {isEdit && (
                  <Button
                    variant="secondary"
                    onClick={handleAddAction}
                    className="flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Action
                  </Button>
                )}
              </div>

              {updatedAnnouncement.actions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No actions added.
                  {isEdit && " Click 'Add Action' to create a button."}
                </p>
              ) : (
                <div className="space-y-2">
                  {updatedAnnouncement.actions.map((action, index) => (
                    <div
                      key={index}
                      className="flex gap-2 items-start p-3 bg-white rounded-lg"
                    >
                      <div className="flex-1 space-y-2">
                        <Input
                          value={action.label}
                          onChange={(e) =>
                            handleActionChange(index, "label", e.target.value)
                          }
                          disabled={!isEdit}
                          placeholder="Button label"
                        />
                        <Input
                          value={action.url || ""}
                          onChange={(e) =>
                            handleActionChange(index, "url", e.target.value)
                          }
                          disabled={!isEdit}
                          placeholder="URL (e.g., /explore or https://example.com)"
                        />
                        <Combobox
                          options={[
                            { value: "primary", label: "Primary" },
                            { value: "secondary", label: "Secondary" },
                          ]}
                          value={{
                            value: action.variant || "primary",
                            label:
                              (action.variant || "primary")
                                .charAt(0)
                                .toUpperCase() +
                              (action.variant || "primary").slice(1),
                          }}
                          onChange={(option: ComboboxOptionI) =>
                            handleActionChange(index, "variant", option.value)
                          }
                          disabled={!isEdit}
                          placeholder="Select variant..."
                        />
                      </div>
                      {isEdit && (
                        <Button.Circle
                          variant="cautionary"
                          onClick={() => handleRemoveAction(index)}
                          className="p-2"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button.Circle>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>

      {/* Cancel Confirmation Dialog */}
      <ConfirmationBox
        isOpen={showCancelDialog}
        onConfirm={handleCancelEdit}
        onCancel={() => setShowCancelDialog(false)}
        onCloseBox={() => setShowCancelDialog(false)}
        title="Cancel Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
        confirmText="Yes, Cancel"
        cancelText="No, Keep Editing"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationBox
        isOpen={showDeleteDialog}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
        onCloseBox={() => setShowDeleteDialog(false)}
        title="Delete Announcement"
        message={`Are you sure you want to delete "${originalAnnouncementData.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Preview modal - shows current announcement as users will see it */}
      <AnnouncementModal
        open={showPreviewModal}
        announcements={[
          {
            _id: originalAnnouncementData._id,
            title: updatedAnnouncement.title,
            message: updatedAnnouncement.message,
            type: updatedAnnouncement.type,
            priority: updatedAnnouncement.priority,
            actions: updatedAnnouncement.actions,
            startsAt: originalAnnouncementData.startsAt,
            expiresAt: originalAnnouncementData.expiresAt,
            createdBy: originalAnnouncementData.createdBy,
            createdAt: originalAnnouncementData.createdAt,
            updatedAt: originalAnnouncementData.updatedAt,
          },
        ]}
        onClose={() => setShowPreviewModal(false)}
        previewMode
      />
    </>
  );
};

export default EditAnnouncementComponent;
